'use client';

import React from 'react';
import { MoreVertical } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { Sheet } from './sheet';

/** Keeps the caller's controls, disabled state and form ownership in the phone sheet. */
export function ActionGroup({
  primary,
  secondary,
}: {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const [formId, setFormId] = React.useState<string>();
  const id = React.useId();
  const flatten = (nodes: React.ReactNode): React.ReactNode[] =>
    React.Children.toArray(nodes).flatMap((node) =>
      React.isValidElement<{ children?: React.ReactNode }>(node) &&
      (node.type === React.Fragment || node.type === 'div')
        ? flatten(node.props.children)
        : [node],
    );
  const secondaryActions = flatten(secondary).filter(Boolean);
  const hasSecondary = secondaryActions.length > 0;
  const associateForm = (node: React.ReactNode): React.ReactNode => {
    if (!React.isValidElement<React.HTMLAttributes<HTMLElement> & { form?: string }>(node))
      return node;
    const props = node.props;
    return React.cloneElement(node, {
      ...(node.type === Button || node.type === 'button' ? { form: props.form ?? formId } : {}),
      ...(props.children ? { children: React.Children.map(props.children, associateForm) } : {}),
    });
  };
  return (
    <div
      data-action-group
      className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 sm:flex-none"
    >
      <div className="hidden min-w-0 max-w-full flex-wrap items-center gap-2 sm:flex">
        {secondary}
      </div>
      {secondaryActions.length === 1 && (
        <div className="order-2 w-full [&>*:not(.sr-only)]:w-full sm:hidden">
          {secondaryActions[0]}
        </div>
      )}
      <div
        className={cn(
          // Button's disabled reason is an sr-only sibling; it keeps its own 1px box rather than
          // the slot's control sizing (full width widened phone pages, auto width overflowed).
          'flex min-w-0 max-w-full flex-1 flex-wrap gap-2 [overflow-wrap:normal] [&>*:not(.sr-only)]:min-h-11 [&>*:not(.sr-only)]:w-full sm:flex-none sm:[&>*:not(.sr-only)]:min-h-0 sm:[&>*:not(.sr-only)]:w-auto',
          secondaryActions.length === 1 && 'basis-full sm:basis-auto',
        )}
      >
        {primary}
      </div>
      {hasSecondary && secondaryActions.length > 1 && (
        <Button
          type="button"
          variant="secondary"
          aria-label="More actions"
          className="shrink-0 sm:hidden"
          onClick={(event) => {
            const form = event.currentTarget.closest('form');
            if (form && !form.id) form.id = id;
            setFormId(form?.id);
            setOpen(true);
          }}
        >
          <MoreVertical aria-hidden="true" /> More
        </Button>
      )}
      <Sheet open={open} onClose={() => setOpen(false)} title="More actions">
        <div className="flex min-w-0 flex-col gap-2 [&_button]:w-full [&_a]:w-full">
          {React.Children.map(secondary, associateForm)}
        </div>
      </Sheet>
    </div>
  );
}
